import _ from 'lodash';
import { createContext } from 'react';

import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';

const api = bindServices('reports');
import {
  generateReportToken,
  updateFavorite,
  handleReportOperation,
  getReportTask,
} from './service';
import { filtersConfig, adminReportsFiltersConfig } from './utils';
import { groupsFiltersConfig } from '@/pages/configurations/report-groups/utils';

import './utils';

export const ReportContext = createContext({});

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  favorites: [],
  adminReports: [],
  groups: [],
  searchCriteria: null,
  currentReport: null,
  currentGroup: null,
  newReport: false,
  newGroup: false,
  pagination: meta,
  groupsPagination: meta,
  adminReportsPagination: meta,
  reportToken: null,
  filtersConfig,
  groupsFiltersConfig,
  reportAdminReportsFiltersConfig: adminReportsFiltersConfig,
  searchAdminReportsCriteria: null,
  favoritesChanged: false,
  exportTasks: {},
  exporting: false,
  // Collaborator slice — backs the shared <CollaboratorsDrawer /> when
  // the user opens it from the reports grid kebab (legacy `/reports`
  // page) or the Launchpad row-command kebab. Living in DVA state lets
  // a single drawer host serve both surfaces.
  collaborators: [],
  collaboratorsReport: null, // { id, title, ... } | null
};

const Model = {
  namespace: 'reports',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/groups',
          ...CoreQueries.ManageReports.groups,
          ...payload,
        });
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    // Main management view (`/reports` sidebar menu) → `/report/manage`.
    // Per the UX guide `/manage` is reserved for top-level management
    // screens accessed from the left sidebar; every other context
    // (selectors, pickers, embedded dialogs) uses `/report/allowed`.
    // `canManage` gates configuration UI; `isFavorite` drives the star
    // icon on each card.
    *fetchReports({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/manage',
          ...CoreQueries.ManageReports.reports,
          select: `${CoreQueries.ManageReports.reports.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryReports',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    // Favorites-first load via the dedicated `/report/favorites`
    // endpoint. `canManage` is required for the row-command kebab and
    // `isFavorite` keeps the star icon filled (the endpoint returns
    // only favorites but the flag isn't auto-included in the select).
    *fetchFavorites({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/favorites',
          ...CoreQueries.ManageReports.favorites,
          select: `${CoreQueries.ManageReports.favorites.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({ type: 'queryFavorites', payload: res });
      } catch (e) {}
    },

    *fetchAdminReports({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/adminReports',
          ...CoreQueries.ManageReports.reports,
          ...payload,
        });
        yield put({
          type: 'queryAdminReports',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchReportById({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: 'report',
          id: payload,
          ...CoreQueries.ManageReports.report,
        });
        yield put({ type: 'queryReportById', payload: res });
      } catch (e) {}
    },

    *powerBIEmbed({ payload }, { call, put }) {
      try {
        const res = yield call(generateReportToken, payload);
        yield put({ type: 'queryPowerBIEmbed', payload: res });
      } catch (e) {}
    },

    // Star toggle — mirrors the `/processes` contract:
    //   1. `querySetFavorite` flips the flag locally (optimistic).
    //   2. `markFavoritesChanged` flags the slice so the next time the
    //      user lands on the favorites tab, `onTabChange` refreshes it.
    // No re-fetch happens here: re-reading `/report/manage` or
    // `/report/favorites` right after the POST/DELETE clobbers the
    // optimistic update with potentially-stale replica data.
    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });
        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `report/activate/${payload.id}/${payload.isActive}`,
          method: 'post',
        });
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    *addNewReport({ payload }, { put }) {
      yield put({ type: 'newReport', payload });
    },

    *addNewGroup({ payload }, { put }) {
      yield put({ type: 'newGroup', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.reports);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *exportReport({ payload }, { call, put }) {
      try {
        yield put({ type: 'setExporting', payload: true });
        yield call(handleReportOperation, payload.reportId, payload.params);
        yield put({ type: 'setExporting', payload: false });
        return true;
      } catch (e) {
        yield put({ type: 'setExporting', payload: false });
        return false;
      }
    },

    *updateExportTask({ payload }, { put }) {
      yield put({ type: 'queryUpdateExportTask', payload });
    },

    *fetchExportTask({ payload }, { call, put }) {
      try {
        const { reportId, taskId } = payload;
        const res = yield call(getReportTask, reportId, taskId);
        if (res) {
          yield put({
            type: 'queryUpdateExportTask',
            payload: {
              reportTaskId: taskId,
              reportId,
              taskStatus: res.status,
              taskOutput: res.taskOutput,
              observations: res.observations,
              taskNumber: res.taskNumber,
            },
          });
        }
      } catch (e) {}
    },

    *cleanReportData(o, { put }) {
      yield put({ type: 'cleanupReportData' });
    },

    *deleteItem({ id, params }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `report/${id}`,
          method: 'delete',
        });
        yield put({
          type: 'queryDeleteItem',
          payload: { id },
        });
        yield put({
          type: 'fetchReports',
          payload: { ...params, readPriority: true },
        });
      } catch (e) {}
    },

    // ─── Collaborators (Report domain) ────────────────────────────────
    //
    // Backend contract (see "AccessControl vs Collaborator" UX guide):
    //   GET    /report/config/collaborators/{reportId}
    //   POST   /report/config/collaborators/{reportId}  body: { collaboratorId }
    //   DELETE /report/config/collaborators/{reportId}/{collaboratorRecordId}
    //
    // Refresh contract: every mutation triggers a single inline GET
    // against the primary replica (`readPriority: true`) so the drawer
    // and the row-command kebab see the row we just POSTed/DELETEd
    // without waiting for the read replica to catch up.
    // ─────────────────────────────────────────────────────────────────

    *fetchReportCollaborators({ payload }, { call, put, select }) {
      try {
        const reportId =
          payload?.reportId || (yield select((s) => s.reports.collaboratorsReport?.id));
        if (!reportId) {
          yield put({ type: 'saveReportCollaborators', payload: [] });
          return;
        }
        const res = yield call(api.getAll, {
          route: `report/config/collaborators/${reportId}`,
          ...(payload?.readPriority && { readPriority: true }),
        });
        const raw = res?.items || res?.data?.items || res || [];
        const list = Array.isArray(raw) ? raw : [];
        // Single batched `user/paged` call keeps the GET contract
        // minimal (no $expand assumed) and works regardless of which
        // navigation property the backend uses for the join.
        const hydrated = yield call(hydrateCollaboratorUsers, list);
        yield put({ type: 'saveReportCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveReportCollaborators', payload: [] });
      }
    },

    *addReportCollaborator({ payload }, { call, put, select }) {
      const reportId =
        payload?.reportId || (yield select((s) => s.reports.collaboratorsReport?.id));
      try {
        if (reportId && payload?.collaboratorId) {
          yield call(api.create, {
            route: `report/config/collaborators/${reportId}`,
            data: { collaboratorId: payload.collaboratorId },
          });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
      if (reportId) {
        yield put({
          type: 'fetchReportCollaborators',
          payload: { reportId, readPriority: true },
        });
      }
    },

    *removeReportCollaborator({ payload }, { call, put, select }) {
      const reportId =
        payload?.reportId || (yield select((s) => s.reports.collaboratorsReport?.id));
      try {
        if (reportId && payload?.id) {
          yield call(api.apiRoute, {
            route: `report/config/collaborators/${reportId}/${payload.id}`,
            method: 'delete',
          });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
      if (reportId) {
        yield put({
          type: 'fetchReportCollaborators',
          payload: { reportId, readPriority: true },
        });
      }
    },

    *resetState(o, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        groupsPagination: {
          ...state.groupsPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryReports(state, { payload }) {
      const data = payload?.items || [];
      return {
        ...state,
        data,
        currentGroup: _.head(data)?.reportGroup,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload,
      };
    },

    queryAdminReports(state, { payload }) {
      return {
        ...state,
        adminReports: payload?.items || [],
        adminReportsPagination: {
          ...state.adminReportsPagination,
          ...toPaginate(payload),
        },
      };
    },

    // Optimistic favorite toggle: flips `isFavorite` on the main grid
    // (`data`) AND inserts/removes the item from the `favorites` slice
    // so the favorites tab reflects the change with zero network
    // traffic. The slice is reconciled the next time the user opens
    // the favorites tab (`onTabChange` → `fetchFavorites`).
    querySetFavorite(state, { payload }) {
      const { id, isFavorite } = payload;
      const next = !isFavorite;
      const updateItem = (item) => (item.id === id ? { ...item, isFavorite: next } : item);

      let favorites = state.favorites;
      if (next) {
        const target = state.data.find((it) => it.id === id);
        if (target && !favorites.some((f) => f.id === id)) {
          favorites = [...favorites, { ...target, isFavorite: true }];
        }
      } else {
        favorites = favorites.filter((f) => f.id !== id);
      }

      return {
        ...state,
        data: state.data.map(updateItem),
        favorites,
      };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        favorites: state.favorites.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        adminReports: (state.adminReports || []).map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    markFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: true,
      };
    },

    clearFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: false,
      };
    },

    queryReportById(state, { payload }) {
      return {
        ...state,
        currentReport: payload,
      };
    },

    queryPowerBIEmbed(state, { payload }) {
      return {
        ...state,
        reportToken: payload,
      };
    },

    queryManagement(state, { payload }) {
      return {
        ...state,
        newGroup: false,
        newReport: false,
        response: payload,
      };
    },

    addNewReport(state, { payload }) {
      return {
        ...state,
        newGroup: false,
        newReport: payload,
      };
    },

    addNewGroup(state, { payload }) {
      return {
        ...state,
        newReport: false,
        newGroup: payload,
      };
    },

    queryDeleteItem(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id),
      };
    },

    saveReportCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },

    // Opens the shared collaborators drawer for `report`. The saga's
    // `select` fallback reads `collaboratorsReport.id` so we can keep
    // the row-command kebab call sites minimal (just one dispatch).
    openCollaboratorsDrawer(state, { payload }) {
      return { ...state, collaboratorsReport: payload || null, collaborators: [] };
    },

    closeCollaboratorsDrawer(state) {
      return { ...state, collaboratorsReport: null, collaborators: [] };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    setExporting(state, { payload }) {
      return { ...state, exporting: payload };
    },

    queryUpdateExportTask(state, { payload }) {
      const taskId = payload.reportTaskId;
      return {
        ...state,
        exportTasks: {
          ...state.exportTasks,
          [taskId]: {
            ...(state.exportTasks[taskId] || {}),
            ...payload,
          },
        },
      };
    },

    cleanupReportData(state) {
      return {
        ...state,
        reportToken: null,
        currentReport: null,
      };
    },

    cleanUp(state) {
      return {
        ...state,
        newGroup: false,
        newReport: false,
        currentGroup: null,
        currentReport: null,
        reportToken: null,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
